/**
 * Global admin boost usage page for /admin/boost-usage route.
 * Shows all workspaces' boost spending and provider costs.
 */

import { useState, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { Navigate } from "react-router-dom";
import { PageHeader } from "../components/layout/PageHeader";
import { useProfile } from "../contexts/ProfileContext";
import { useAdminAllWorkspacesBoostSummary } from "../hooks/useAdminWorkspaces";
import { adminRoutes, publicRoutes } from "../lib/routes";
import {
  AlertCircle,
  Loader2,
  Zap,
  DollarSign,
  ArrowUpDown,
} from "lucide-react";

type SortField = "workspaceName" | "totalBoostsSpent" | "totalProviderCostUsd" | "transactionCount";
type SortDir = "asc" | "desc";

function formatCurrency(value: number): string {
  return `$${value.toFixed(2)}`;
}

function formatNumber(value: number): string {
  return value.toLocaleString();
}

export function AdminBoostUsage() {
  const navigate = useNavigate();
  const { isAdmin, isLoading: isProfileLoading } = useProfile();
  const [sortField, setSortField] = useState<SortField>("totalBoostsSpent");
  const [sortDir, setSortDir] = useState<SortDir>("desc");

  const { data, isLoading, error } = useAdminAllWorkspacesBoostSummary();

  if (!isProfileLoading && !isAdmin) {
    return <Navigate to={publicRoutes.home} replace />;
  }

  const sorted = useMemo(() => {
    if (!data?.workspaces) return [];
    return [...data.workspaces].sort((a, b) => {
      const aVal = a[sortField] ?? "";
      const bVal = b[sortField] ?? "";
      if (aVal < bVal) return sortDir === "asc" ? -1 : 1;
      if (aVal > bVal) return sortDir === "asc" ? 1 : -1;
      return 0;
    });
  }, [data, sortField, sortDir]);

  const totals = useMemo(() => {
    if (!data?.workspaces) return { boosts: 0, cost: 0, transactions: 0 };
    return data.workspaces.reduce(
      (acc, ws) => ({
        boosts: acc.boosts + ws.totalBoostsSpent,
        cost: acc.cost + ws.totalProviderCostUsd,
        transactions: acc.transactions + ws.transactionCount,
      }),
      { boosts: 0, cost: 0, transactions: 0 }
    );
  }, [data]);

  const handleSort = (field: SortField) => {
    if (sortField === field) {
      setSortDir((d) => (d === "asc" ? "desc" : "asc"));
    } else {
      setSortField(field);
      setSortDir("desc");
    }
  };

  const SortHeader = ({ field, label }: { field: SortField; label: string }) => (
    <th
      className="px-4 py-2 font-medium text-muted-foreground cursor-pointer hover:text-foreground select-none"
      onClick={() => handleSort(field)}
    >
      <span className="inline-flex items-center gap-1">
        {label}
        <ArrowUpDown className={`h-3 w-3 ${sortField === field ? "text-foreground" : "text-muted-foreground/40"}`} />
      </span>
    </th>
  );

  return (
    <div>
      <PageHeader
        title="Boost Usage"
        description="Global boost spending and provider costs across all workspaces"
        isAdmin
        breadcrumbs={[
          { label: "Dashboard", href: publicRoutes.home },
          { label: "Admin" },
          { label: "Boost Usage" },
        ]}
      />

      {isLoading ? (
        <div className="flex items-center justify-center py-12">
          <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
          <span className="ml-2 text-sm text-muted-foreground">Loading boost data...</span>
        </div>
      ) : error ? (
        <div className="flex items-center gap-3 p-4 bg-red-50 dark:bg-red-950 border border-red-200 dark:border-red-800 rounded-lg">
          <AlertCircle className="h-5 w-5 text-red-600 shrink-0" />
          <div>
            <p className="text-sm font-medium text-red-800 dark:text-red-200">Failed to load boost summary</p>
            <p className="text-xs text-red-600">{error.message}</p>
          </div>
        </div>
      ) : (
        <>
          {/* Global Totals */}
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-6">
            <div className="p-4 bg-orange-50 dark:bg-orange-950/30 border border-orange-200 dark:border-orange-800 rounded-lg">
              <div className="flex items-center gap-2 mb-1">
                <Zap className="h-4 w-4 text-orange-600" />
                <span className="text-xs font-medium text-orange-700 dark:text-orange-300 uppercase tracking-wide">
                  Total Boosts Spent
                </span>
              </div>
              <p className="text-2xl font-bold text-orange-900 dark:text-orange-100">
                {formatNumber(totals.boosts)}
              </p>
            </div>
            <div className="p-4 bg-green-50 dark:bg-green-950/30 border border-green-200 dark:border-green-800 rounded-lg">
              <div className="flex items-center gap-2 mb-1">
                <DollarSign className="h-4 w-4 text-green-600" />
                <span className="text-xs font-medium text-green-700 dark:text-green-300 uppercase tracking-wide">
                  Total Provider Cost
                </span>
              </div>
              <p className="text-2xl font-bold text-green-900 dark:text-green-100">
                {formatCurrency(totals.cost)}
              </p>
            </div>
            <div className="p-4 bg-blue-50 dark:bg-blue-950/30 border border-blue-200 dark:border-blue-800 rounded-lg">
              <div className="flex items-center gap-2 mb-1">
                <Zap className="h-4 w-4 text-blue-600" />
                <span className="text-xs font-medium text-blue-700 dark:text-blue-300 uppercase tracking-wide">
                  Total Transactions
                </span>
              </div>
              <p className="text-2xl font-bold text-blue-900 dark:text-blue-100">
                {formatNumber(totals.transactions)}
              </p>
            </div>
          </div>

          {/* Workspaces Table */}
          <div className="bg-card rounded-lg border border-border overflow-hidden">
            {sorted.length === 0 ? (
              <p className="text-sm text-muted-foreground p-6">No boost usage data found.</p>
            ) : (
              <table className="w-full text-sm">
                <thead>
                  <tr className="bg-muted/50">
                    <SortHeader field="workspaceName" label="Workspace" />
                    <SortHeader field="totalBoostsSpent" label="Boosts Spent" />
                    <SortHeader field="totalProviderCostUsd" label="Provider Cost (USD)" />
                    <SortHeader field="transactionCount" label="Transactions" />
                  </tr>
                </thead>
                <tbody>
                  {sorted.map((ws) => (
                    <tr
                      key={ws.workspaceId}
                      className="border-t border-border hover:bg-accent/50 cursor-pointer transition-colors"
                      onClick={() => navigate(`${adminRoutes.workspace(ws.workspaceId)}?tab=usage`)}
                    >
                      <td className="px-4 py-3 font-medium">
                        {ws.workspaceName || ws.workspaceId}
                      </td>
                      <td className="px-4 py-3 text-right">{formatNumber(ws.totalBoostsSpent)}</td>
                      <td className="px-4 py-3 text-right">{formatCurrency(ws.totalProviderCostUsd)}</td>
                      <td className="px-4 py-3 text-right">{formatNumber(ws.transactionCount)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        </>
      )}
    </div>
  );
}
